import { useEffect, useState } from "react";
import { getPreprint, preprintPdfUrl, PreprintRow } from "../lib/api";

/**
 * Preprint tab (#190): renders the manuscript PDF inline.
 *
 * The backend `/api/preprints/{id}/pdf` endpoint streams the host-resolved PDF
 * (Copernicus convention URL / arXiv) same-origin, so we can `<embed>` it the
 * same way `BinaryViewer` embeds deposit PDFs — no cross-origin framing issues.
 * For a host with no buildable direct-PDF URL (EarthArXiv/other,
 * `paper_pdf_available === false`) we link out to the preprint page instead.
 */
export default function PreprintPane({ egusphereId }: { egusphereId: string }) {
  const [row, setRow] = useState<PreprintRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRow(null);
    setError(null);
    getPreprint(egusphereId)
      .then((d) => {
        if (!cancelled) setRow(d.preprint);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [egusphereId]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-sm text-ink-500">
        Couldn’t load the preprint: {error}
      </div>
    );
  }
  if (!row) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-ink-500">
        Loading preprint…
      </div>
    );
  }
  if (!row.paper_pdf_available) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-sm text-ink-500">
        <div>Inline preview isn’t available for this preprint’s host yet.</div>
        <a
          className="text-ink-700 underline"
          href={row.landing_url}
          target="_blank"
          rel="noreferrer"
        >
          Open the preprint page ↗
        </a>
      </div>
    );
  }
  return (
    <embed
      src={preprintPdfUrl(egusphereId)}
      type="application/pdf"
      title="Preprint PDF"
      className="h-full w-full"
    />
  );
}
